"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Download,
  Filter,
  Search,
  AlertTriangle,
  FileX,
  UserX,
  FileText,
  ExternalLink,
  Edit,
  CheckCircle,
  XCircle,
} from "lucide-react"
import { Link } from "react-router-dom"
import bgImage from "@/assets/favicon.png"

interface PendingRecord {
  id: string
  type: "GD_WITHOUT_SECTIONS" | "GD_WITHOUT_SUSPECTS" | "PDF_WITHOUT_OCR" | "DUPLICATE_FILES" | "MISSING_VICTIM"
  gdNumber?: string
  fileName?: string
  uploadDate: string
  officerName: string
  policeStation: string
  priority: "HIGH" | "MEDIUM" | "LOW"
  description: string
  actionRequired: string
  estimatedTime: string
  status: "PENDING" | "IN_PROGRESS" | "RESOLVED"
}

// Dummy data
const pendingRecordsData: PendingRecord[] = [
  {
    id: "1",
    type: "GD_WITHOUT_SECTIONS",
    gdNumber: "GD/2024/007",
    uploadDate: "20/01/2024",
    officerName: "Sub-Inspector Patel",
    policeStation: "Andheri",
    priority: "HIGH",
    description: "GD entry created but no legal sections specified",
    actionRequired: "Add appropriate legal sections (IPC/BNS)",
    estimatedTime: "5 minutes",
    status: "PENDING",
  },
  {
    id: "2",
    type: "GD_WITHOUT_SUSPECTS",
    gdNumber: "GD/2024/008",
    uploadDate: "21/01/2024",
    officerName: "Inspector Sharma",
    policeStation: "Bandra",
    priority: "MEDIUM",
    description: "GD entry missing suspect information",
    actionRequired: "Add suspect details or mark as unknown",
    estimatedTime: "10 minutes",
    status: "IN_PROGRESS",
  },
  {
    id: "3",
    type: "PDF_WITHOUT_OCR",
    fileName: "complaint_document_009.pdf",
    uploadDate: "19/01/2024",
    officerName: "ASI Kumar",
    policeStation: "Borivali",
    priority: "MEDIUM",
    description: "PDF uploaded but OCR processing failed",
    actionRequired: "Re-upload with better quality or manual entry",
    estimatedTime: "15 minutes",
    status: "PENDING",
  },
  {
    id: "4",
    type: "DUPLICATE_FILES",
    fileName: "case_file_duplicate.pdf",
    uploadDate: "18/01/2024",
    officerName: "Constable Singh",
    policeStation: "Malad",
    priority: "LOW",
    description: "Duplicate file detected in system",
    actionRequired: "Review and remove duplicate entry",
    estimatedTime: "3 minutes",
    status: "PENDING",
  },
  {
    id: "5",
    type: "MISSING_VICTIM",
    gdNumber: "GD/2024/009",
    uploadDate: "22/01/2024",
    officerName: "Inspector Verma",
    policeStation: "Malad",
    priority: "HIGH",
    description: "Criminal case without victim information",
    actionRequired: "Add victim details or update case type",
    estimatedTime: "8 minutes",
    status: "PENDING",
  },
  {
    id: "6",
    type: "GD_WITHOUT_SECTIONS",
    gdNumber: "GD/2024/010",
    uploadDate: "23/01/2024",
    officerName: "Sub-Inspector Desai",
    policeStation: "Borivali",
    priority: "HIGH",
    description: "Fraud case missing legal sections",
    actionRequired: "Add IPC 420 and related sections",
    estimatedTime: "5 minutes",
    status: "RESOLVED",
  },
]

export default function PendingRecordsReport() {
  const [filteredData, setFilteredData] = useState(pendingRecordsData)
  const [filters, setFilters] = useState({
    type: "",
    priority: "",
    status: "",
    policeStation: "",
    officer: "",
  })
  const [searchTerm, setSearchTerm] = useState("")

  const recordTypes = [
    "All",
    "GD_WITHOUT_SECTIONS",
    "GD_WITHOUT_SUSPECTS",
    "PDF_WITHOUT_OCR",
    "DUPLICATE_FILES",
    "MISSING_VICTIM",
  ]
  const priorities = ["All", "HIGH", "MEDIUM", "LOW"]
  const statuses = ["All", "PENDING", "IN_PROGRESS", "RESOLVED"]
  const policeStations = ["All", "Bandra", "Andheri", "Borivali", "Malad"]

  const applyFilters = () => {
    let filtered = pendingRecordsData

    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.gdNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.fileName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.officerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }

    if (filters.type && filters.type !== "All") {
      filtered = filtered.filter((item) => item.type === filters.type)
    }

    if (filters.priority && filters.priority !== "All") {
      filtered = filtered.filter((item) => item.priority === filters.priority)
    }

    if (filters.status && filters.status !== "All") {
      filtered = filtered.filter((item) => item.status === filters.status)
    }

    if (filters.policeStation && filters.policeStation !== "All") {
      filtered = filtered.filter((item) => item.policeStation === filters.policeStation)
    }

    setFilteredData(filtered)
  }

  const getTypeBadge = (type: string) => {
    const typeConfig = {
      GD_WITHOUT_SECTIONS: { color: "bg-red-100 text-red-800", icon: FileX, text: "Missing Sections" },
      GD_WITHOUT_SUSPECTS: { color: "bg-orange-100 text-orange-800", icon: UserX, text: "Missing Suspects" },
      PDF_WITHOUT_OCR: { color: "bg-yellow-100 text-yellow-800", icon: FileText, text: "OCR Failed" },
      DUPLICATE_FILES: { color: "bg-purple-100 text-purple-800", icon: AlertTriangle, text: "Duplicate Files" },
      MISSING_VICTIM: { color: "bg-pink-100 text-pink-800", icon: UserX, text: "Missing Victim" },
    }
    const config = typeConfig[type as keyof typeof typeConfig]
    const IconComponent = config.icon
    return (
      <Badge className={`${config.color} hover:${config.color}`}>
        <IconComponent className="h-3 w-3 mr-1" />
        {config.text}
      </Badge>
    )
  }

  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      HIGH: { color: "bg-red-100 text-red-800", text: "High" },
      MEDIUM: { color: "bg-yellow-100 text-yellow-800", text: "Medium" },
      LOW: { color: "bg-green-100 text-green-800", text: "Low" },
    }
    const config = priorityConfig[priority as keyof typeof priorityConfig]
    return <Badge className={`${config.color} hover:${config.color}`}>{config.text}</Badge>
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      PENDING: { color: "bg-red-100 text-red-800", icon: XCircle, text: "Pending" },
      IN_PROGRESS: { color: "bg-yellow-100 text-yellow-800", icon: Edit, text: "In Progress" },
      RESOLVED: { color: "bg-green-100 text-green-800", icon: CheckCircle, text: "Resolved" },
    }
    const config = statusConfig[status as keyof typeof statusConfig]
    const IconComponent = config.icon
    return (
      <Badge className={`${config.color} hover:${config.color}`}>
        <IconComponent className="h-3 w-3 mr-1" />
        {config.text}
      </Badge>
    )
  }

  const exportToCSV = () => {
    const headers = [
      "Type",
      "GD Number",
      "File Name",
      "Upload Date",
      "Officer Name",
      "Police Station",
      "Priority",
      "Description",
      "Action Required",
      "Estimated Time",
      "Status",
    ]
    const csvContent = [
      headers.join(","),
      ...filteredData.map((row) =>
        [
          row.type,
          row.gdNumber || "",
          row.fileName || "",
          row.uploadDate,
          row.officerName,
          row.policeStation,
          row.priority,
          row.description,
          row.actionRequired,
          row.estimatedTime,
          row.status,
        ].join(","),
      ),
    ].join("\n")

    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `pending-records-${new Date().toISOString().split("T")[0]}.csv`
    a.click()
  }

  const totalPending = filteredData.filter((item) => item.status === "PENDING").length
  const inProgress = filteredData.filter((item) => item.status === "IN_PROGRESS").length
  const highPriority = filteredData.filter((item) => item.priority === "HIGH").length
  const resolved = filteredData.filter((item) => item.status === "RESOLVED").length

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#0d2846]/3 via-[#e2af58]/10 to-[#0d2846]/3 p-6">
      <div
        className="absolute inset-0 bg-no-repeat bg-center bg-contain opacity-3 pointer-events-none"
        style={{
          backgroundImage: `url(${bgImage})`,
        }}
      />
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <Link to="/reports" className="text-blue-600 hover:text-blue-800 text-sm mb-2 inline-block">
              ← Back to Reports
            </Link>
            <h1 className="text-3xl font-bold text-black">Pending/Incomplete Records Report</h1>
            <p className="text-gray-600 mt-1">Identify records with missing data and direct action links</p>
          </div>
          <Button onClick={exportToCSV} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <XCircle className="h-5 w-5 text-red-600" />
                <div>
                  <div className="text-2xl font-bold text-red-600">{totalPending}</div>
                  <p className="text-sm text-gray-600">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Edit className="h-5 w-5 text-yellow-600" />
                <div>
                  <div className="text-2xl font-bold text-yellow-600">{inProgress}</div>
                  <p className="text-sm text-gray-600">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
                <div>
                  <div className="text-2xl font-bold text-orange-600">{highPriority}</div>
                  <p className="text-sm text-gray-600">High Priority</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <div className="text-2xl font-bold text-green-600">{resolved}</div>
                  <p className="text-sm text-gray-600">Resolved</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Priority Alert */}
        {highPriority > 0 && (
          <Card className="border border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <div>
                  <p className="font-medium text-red-800">
                    {highPriority} high priority record{highPriority > 1 ? "s" : ""} require
                    {highPriority === 1 ? "s" : ""} immediate attention
                  </p>
                  <p className="text-sm text-red-600">
                    These records may impact case processing and should be resolved as soon as possible.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg text-black flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters & Search
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Record Type</Label>
                <Select value={filters.type} onValueChange={(value) => setFilters({ ...filters, type: value })}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {recordTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Priority</Label>
                <Select value={filters.priority} onValueChange={(value) => setFilters({ ...filters, priority: value })}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {priorities.map((priority) => (
                      <SelectItem key={priority} value={priority}>
                        {priority}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Status</Label>
                <Select value={filters.status} onValueChange={(value) => setFilters({ ...filters, status: value })}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {statuses.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Police Station</Label>
                <Select
                  value={filters.policeStation}
                  onValueChange={(value) => setFilters({ ...filters, policeStation: value })}
                >
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Select station" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {policeStations.map((station) => (
                      <SelectItem key={station} value={station}>
                        {station}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search records..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 border-gray-300"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={applyFilters} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
                Apply Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="all" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 bg-gray-100">
            <TabsTrigger value="all" className="data-[state=active]:bg-white">
              All Records ({filteredData.length})
            </TabsTrigger>
            <TabsTrigger value="high-priority" className="data-[state=active]:bg-white">
              High Priority ({highPriority})
            </TabsTrigger>
            <TabsTrigger value="pending" className="data-[state=active]:bg-white">
              Pending ({totalPending})
            </TabsTrigger>
            <TabsTrigger value="in-progress" className="data-[state=active]:bg-white">
              In Progress ({inProgress})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black">
                  All Pending Records ({filteredData.length} records)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-black font-semibold">Type</TableHead>
                        <TableHead className="text-black font-semibold">Reference</TableHead>
                        <TableHead className="text-black font-semibold">Upload Date</TableHead>
                        <TableHead className="text-black font-semibold">Officer</TableHead>
                        <TableHead className="text-black font-semibold">Station</TableHead>
                        <TableHead className="text-black font-semibold">Priority</TableHead>
                        <TableHead className="text-black font-semibold">Description</TableHead>
                        <TableHead className="text-black font-semibold">Est. Time</TableHead>
                        <TableHead className="text-black font-semibold">Status</TableHead>
                        <TableHead className="text-black font-semibold">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredData.map((item) => (
                        <TableRow key={item.id} className="hover:bg-gray-50">
                          <TableCell>{getTypeBadge(item.type)}</TableCell>
                          <TableCell className="text-gray-600">
                            {item.gdNumber ? (
                              <Button variant="link" className="p-0 h-auto text-blue-600 hover:text-blue-800">
                                {item.gdNumber}
                                <ExternalLink className="h-3 w-3 ml-1" />
                              </Button>
                            ) : (
                              <span className="text-sm">{item.fileName}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-gray-600">{item.uploadDate}</TableCell>
                          <TableCell className="text-gray-600">{item.officerName}</TableCell>
                          <TableCell className="text-gray-600">{item.policeStation}</TableCell>
                          <TableCell>{getPriorityBadge(item.priority)}</TableCell>
                          <TableCell className="text-gray-600 max-w-xs">
                            <div className="truncate" title={item.description}>
                              {item.description}
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-600">{item.estimatedTime}</TableCell>
                          <TableCell>{getStatusBadge(item.status)}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              {item.status === "PENDING" && (
                                <Button size="sm" className="bg-blue-600 text-white hover:bg-blue-700">
                                  Complete
                                </Button>
                              )}
                              {item.status === "IN_PROGRESS" && (
                                <Button size="sm" className="bg-green-600 text-white hover:bg-green-700">
                                  Finish
                                </Button>
                              )}
                              {item.status === "RESOLVED" && (
                                <Button variant="ghost" size="sm" className="hover:bg-gray-100">
                                  View
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="high-priority">
            <Card className="border border-red-200">
              <CardHeader>
                <CardTitle className="text-lg text-black flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  High Priority Records - Immediate Action Required
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filteredData
                    .filter((item) => item.priority === "HIGH")
                    .map((item) => (
                      <div key={item.id} className="p-4 border border-red-200 rounded-lg bg-red-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {getTypeBadge(item.type)}
                              {getPriorityBadge(item.priority)}
                              {getStatusBadge(item.status)}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <p className="font-medium text-black">
                                  {item.gdNumber ? `GD: ${item.gdNumber}` : `File: ${item.fileName}`}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {item.officerName} - {item.policeStation}
                                </p>
                                <p className="text-sm text-gray-600">Uploaded: {item.uploadDate}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-800 mb-1">
                                  <strong>Issue:</strong> {item.description}
                                </p>
                                <p className="text-sm text-gray-800 mb-1">
                                  <strong>Action:</strong> {item.actionRequired}
                                </p>
                                <p className="text-sm text-gray-600">
                                  <strong>Est. Time:</strong> {item.estimatedTime}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="ml-4">
                            {item.status === "PENDING" && (
                              <Button className="bg-red-600 text-white hover:bg-red-700">Complete Now</Button>
                            )}
                            {item.status === "IN_PROGRESS" && (
                              <Button className="bg-orange-600 text-white hover:bg-orange-700">Finish</Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pending">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black">Pending Records ({totalPending} records)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-black font-semibold">Type</TableHead>
                        <TableHead className="text-black font-semibold">Reference</TableHead>
                        <TableHead className="text-black font-semibold">Officer</TableHead>
                        <TableHead className="text-black font-semibold">Priority</TableHead>
                        <TableHead className="text-black font-semibold">Action Required</TableHead>
                        <TableHead className="text-black font-semibold">Est. Time</TableHead>
                        <TableHead className="text-black font-semibold">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredData
                        .filter((item) => item.status === "PENDING")
                        .map((item) => (
                          <TableRow key={item.id} className="hover:bg-gray-50">
                            <TableCell>{getTypeBadge(item.type)}</TableCell>
                            <TableCell className="text-gray-600">
                              {item.gdNumber ? item.gdNumber : item.fileName}
                            </TableCell>
                            <TableCell className="text-gray-600">{item.officerName}</TableCell>
                            <TableCell>{getPriorityBadge(item.priority)}</TableCell>
                            <TableCell className="text-gray-600 max-w-xs">
                              <div className="truncate" title={item.actionRequired}>
                                {item.actionRequired}
                              </div>
                            </TableCell>
                            <TableCell className="text-gray-600">{item.estimatedTime}</TableCell>
                            <TableCell>
                              <Button size="sm" className="bg-blue-600 text-white hover:bg-blue-700">
                                Complete GD
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="in-progress">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black">In Progress Records ({inProgress} records)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-black font-semibold">Type</TableHead>
                        <TableHead className="text-black font-semibold">Reference</TableHead>
                        <TableHead className="text-black font-semibold">Officer</TableHead>
                        <TableHead className="text-black font-semibold">Priority</TableHead>
                        <TableHead className="text-black font-semibold">Description</TableHead>
                        <TableHead className="text-black font-semibold">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredData
                        .filter((item) => item.status === "IN_PROGRESS")
                        .map((item) => (
                          <TableRow key={item.id} className="hover:bg-gray-50">
                            <TableCell>{getTypeBadge(item.type)}</TableCell>
                            <TableCell className="text-gray-600">
                              {item.gdNumber ? item.gdNumber : item.fileName}
                            </TableCell>
                            <TableCell className="text-gray-600">{item.officerName}</TableCell>
                            <TableCell>{getPriorityBadge(item.priority)}</TableCell>
                            <TableCell className="text-gray-600 max-w-xs">
                              <div className="truncate" title={item.description}>
                                {item.description}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Button size="sm" className="bg-green-600 text-white hover:bg-green-700">
                                Finish
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
